// ==============================================
// User Devices Page
// ==============================================

import { useRef, useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  ServerIcon,
  PlusIcon,
  ClockIcon,
  XCircleIcon,
  SignalIcon,
  SignalSlashIcon,
  PauseCircleIcon,
  TrashIcon,
  ArrowPathIcon,
  ChevronRightIcon,
  KeyIcon,
  MinusCircleIcon,
  PlusCircleIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { InlineRename } from '../../components/ui/InlineRename';
import { copyToClipboard } from '../../lib/clipboard';
import { AddDeviceModal } from '../../components/devices/AddDeviceModal';
import { RegistrationCodePanel } from '../../components/devices/RegistrationCodePanel';
import { ScoutIcon } from '../../components/devices/ScoutIcon';
import { ModalShell } from '../../components/ui/ModalShell';
import { SgDevicePanel } from '../../components/devices/SgDevicePanel';
import { SharedSgList } from '../../components/devices/SharedSgList';
import { useToastStore } from '../../store/toastStore';
import { formatRelativeTime } from '../../utils/formatters';
import type { Device } from '../../types/device';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;
type Tone = 'ok' | 'neutral' | 'caution' | 'alarm';

// Four colours only: green, grey, orange, red (Jason, 9 Oct 2026, Scout status).
const TONE: Record<Tone, string> = {
  ok: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400',
  neutral: 'border-slate-600 bg-slate-700/40 text-slate-300',
  caution: 'border-amber-500/40 bg-amber-500/10 text-amber-400',
  alarm: 'border-red-500/40 bg-red-500/10 text-red-400',
};

const isGatewaySerial = (serial: string) => /^SG-[0-9A-F]{16}$/.test(serial);

function kindOf(device: Device): string {
  if (isGatewaySerial(device.serial_number)) return 'Scout Gateway';
  return device.device_type === 'scout' ? 'Scout' : 'Command Hub';
}

const isOnlineNow = (device: Device) =>
  !!device.last_seen_at && new Date(device.last_seen_at) > new Date(Date.now() - 5 * 60 * 1000);

const codeExpired = (device: Device) =>
  !!device.registration_code_expires_at && new Date(device.registration_code_expires_at) < new Date();

/** "9 Oct 2026, 6:11pm": the full time without seconds or zone. */
const fullTime = (value: string) => format(new Date(value), 'd MMM yyyy, h:mmaaa');

/** "9 Oct, 6:11pm" this year, else "9 Oct 2025": fits a half-width tile on a phone. */
const shortTime = (value: string) => {
  const d = new Date(value);
  return format(d, d.getFullYear() === new Date().getFullYear() ? 'd MMM, h:mmaaa' : 'd MMM yyyy');
};

/** One status a customer can read: registration state first, then whether it's connected. */
function statusOf(device: Device): { label: string; tone: Tone; icon: Icon } {
  switch (device.status) {
    case 'pending':
      return { label: 'Waiting to register', tone: 'caution', icon: ClockIcon };
    case 'unregistered':
      return { label: 'Not registered', tone: 'neutral', icon: ClockIcon };
    case 'suspended':
      return { label: 'Suspended', tone: 'caution', icon: PauseCircleIcon };
    case 'revoked':
      return { label: 'Removed', tone: 'alarm', icon: XCircleIcon };
    default:
      return isOnlineNow(device)
        ? { label: 'Online', tone: 'ok', icon: SignalIcon }
        : { label: 'Offline', tone: 'neutral', icon: SignalSlashIcon };
  }
}

/** Status is a word plus a glyph, never a glyph alone. */
function StatusChip({ device }: { device: Device }) {
  const { label, tone, icon: Glyph } = statusOf(device);
  return (
    <span className={`inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 text-sm font-medium ${TONE[tone]}`}>
      <Glyph className="h-4 w-4" aria-hidden="true" />
      {label}
    </span>
  );
}

/** The device drawn in its tile: the Scout itself, or a server for a gateway or hub. */
function DeviceGlyph({ device, size = 'md' }: { device: Device; size?: 'md' | 'lg' }) {
  const scout = device.device_type === 'scout' && !isGatewaySerial(device.serial_number);
  const Glyph = scout ? ScoutIcon : ServerIcon;
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-xl border border-slate-700/60 bg-slate-900/60 ${
        size === 'lg' ? 'h-16 w-16' : 'h-14 w-14'
      } ${scout ? 'text-cyan-400' : 'text-slate-300'}`}
    >
      <Glyph className={scout ? (size === 'lg' ? 'h-11 w-11' : 'h-10 w-10') : 'h-7 w-7'} />
    </div>
  );
}

export function DevicesPage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['devices', 'my'],
    queryFn: () => api.getMyDevices(),
  });

  const devices = data?.devices || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">My devices</h1>
          <p className="text-slate-400">Manage your registered devices</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Register device
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : devices.length === 0 ? (
        <EmptyState onAddDevice={() => setShowAddModal(true)} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {devices.map((device) => (
            <DeviceCard
              key={device.id}
              device={device}
              onClick={() => setSelectedDevice(device)}
            />
          ))}
        </div>
      )}

      {/* SGs shared with me, as crew or fleet operator */}
      <SharedSgList />

      {/* Add Device Modal */}
      {showAddModal && (
        <AddDeviceModal onClose={() => setShowAddModal(false)} />
      )}

      {/* Device Details Modal */}
      {selectedDevice && (
        <DeviceDetailsModal
          device={selectedDevice}
          onClose={() => setSelectedDevice(null)}
        />
      )}
    </div>
  );
}

function EmptyState({ onAddDevice }: { onAddDevice: () => void }) {
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-slate-900/60 border border-slate-700/60 flex items-center justify-center">
        <ScoutIcon className="w-11 h-11 text-slate-400" />
      </div>
      <h3 className="text-lg font-medium text-white mb-2">No devices registered</h3>
      <p className="text-slate-400 mb-6 max-w-md mx-auto">
        Register your Vortex devices to access offline charts, sync your routes, and manage your
        fleet.
      </p>
      <button
        onClick={onAddDevice}
        className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
      >
        <PlusIcon className="w-5 h-5" />
        Register device
      </button>
    </div>
  );
}

/** The one line under a card's status: the code while it waits, else when it was last heard from. */
function cardNote(device: Device, gateway: boolean): ReactNode {
  if (device.status === 'pending' && device.registration_code && !gateway) {
    return codeExpired(device) ? (
      <span className="text-red-400">Code expired</span>
    ) : (
      <span>
        Code <span className="font-mono font-semibold tracking-wider text-cyan-400">{device.registration_code}</span>
      </span>
    );
  }
  if (!device.last_seen_at) return null;
  return (
    <span title={fullTime(device.last_seen_at)}>
      {isOnlineNow(device) ? 'Seen' : 'Last seen'} {formatRelativeTime(device.last_seen_at)}
    </span>
  );
}

function DeviceCard({ device, onClick }: { device: Device; onClick: () => void }) {
  const gateway = isGatewaySerial(device.serial_number);
  const note = cardNote(device, gateway);

  return (
    <button
      type="button"
      onClick={onClick}
      className="group w-full text-left rounded-xl border border-slate-700 bg-slate-800/50 p-4 sm:p-5 hover:border-slate-600 hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 transition-colors"
    >
      <div className="flex items-center gap-4">
        <DeviceGlyph device={device} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-white">{device.name || device.serial_number}</h3>
          <p className="text-sm text-slate-400">{kindOf(device)}</p>
        </div>
        <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-500 transition-colors group-hover:text-slate-300" aria-hidden="true" />
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 text-sm text-slate-400">
        <StatusChip device={device} />
        {note}
      </div>
    </button>
  );
}

async function copyField(value: string, label: string): Promise<void> {
  const ok = await copyToClipboard(value);
  useToastStore.getState().addToast(
    ok
      ? { type: 'success', title: 'Copied', message: `${label} copied.` }
      : { type: 'error', title: 'Copy failed', message: `Select the ${label.toLowerCase()} and copy it manually.` },
  );
}

/** A key figure: what it is, when (relative, large), and the exact time under it. */
function Figure({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="min-w-0">
      <p className="text-sm text-slate-400">{label}</p>
      <p className="mt-1 text-base font-semibold text-white">{value ? formatRelativeTime(value) : 'Never'}</p>
      {value && <p className="text-sm text-slate-400" title={fullTime(value)}>{shortTime(value)}</p>}
    </div>
  );
}

/** One line in the details well: no ruled lines; stacked on a phone so a long serial keeps its line. */
function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <dt className="shrink-0 text-sm text-slate-400">{label}</dt>
      <dd className="min-w-0 sm:text-right">{children}</dd>
    </div>
  );
}

/** A copyable identifier: monospace value with a copy button beside it. */
function CopyValue({ value, label, name }: { value: string; label: string; name: string }) {
  return (
    <div className="flex min-w-0 items-center gap-1 sm:justify-end">
      <span className="break-all font-mono text-sm text-white">{value}</span>
      <button
        type="button"
        onClick={() => copyField(value, label)}
        className="-my-1 -mr-2 shrink-0 rounded-md p-2 text-slate-400 transition-colors hover:bg-slate-700 hover:text-white"
        title={`Copy ${name}`}
        aria-label={`Copy ${name}`}
      >
        <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

/** What a confirmation will do, one fact a line. Each glyph sits under the
 * lead's tile, so the facts' text lines up with the lead sentence. */
function Facts({ items }: { items: { icon: Icon; text: string }[] }) {
  return (
    <ul className="space-y-3">
      {items.map(({ icon: Glyph, text }) => (
        <li key={text} className="flex gap-4 text-sm text-slate-200">
          <span className="flex h-5 w-11 shrink-0 justify-center">
            <Glyph className="h-5 w-5 text-slate-400" aria-hidden="true" />
          </span>
          <span>{text}</span>
        </li>
      ))}
    </ul>
  );
}

/** A confirmation's head: the action's glyph in its tone, and one plain sentence. */
function Lead({ icon: Glyph, tone, children }: { icon: Icon; tone: Tone; children: ReactNode }) {
  return (
    <div className="flex items-center gap-4">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${TONE[tone]}`}>
        <Glyph className="h-6 w-6" aria-hidden="true" />
      </div>
      <p className="text-base text-slate-200">{children}</p>
    </div>
  );
}

/** The server's own reason when it gave one (e.g. a plan limit), else a plain fallback. */
const reasonOf = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

/** What the status means for the customer, in one line under the chip. */
function statusLine(device: Device, gateway: boolean): string | null {
  switch (device.status) {
    case 'pending':
      if (gateway) return null;
      // An expired code says so in its own panel.
      return device.registration_code && !codeExpired(device) ? 'Enter this code on it to finish.' : null;
    case 'suspended':
      return "It can't sync while it's suspended.";
    default:
      // Online or Offline says it all; the figures below say when.
      return null;
  }
}

type ModalStep = 'details' | 'confirm-code' | 'new-code' | 'confirm-remove';

export function DeviceDetailsModal({ device, onClose }: { device: Device; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<ModalStep>('details');
  // The modal holds a snapshot of the device; show a rename straight away.
  const [shownName, setShownName] = useState(device.name || device.serial_number);
  // Open on the details, not on a ringed close button.
  const bodyRef = useRef<HTMLDivElement>(null);
  const gateway = isGatewaySerial(device.serial_number);
  const kind = kindOf(device);
  // A Scout Gateway gets its code through its own sign-in, never typed in, so
  // a new code from here would leave it with no way back (SCOUT_GATEWAY_ENROLMENT.md).
  const canGetNewCode = !gateway;

  const updateMutation = useMutation({
    mutationFn: (name: string) => api.updateDevice(device.id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => api.deleteDevice(device.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      onClose();
    },
  });

  // Owner-scoped: the admin route 403s for customers (issue #753).
  const newCodeMutation = useMutation({
    mutationFn: () => api.unlinkDevice(device.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setStep('new-code');
    },
  });

  const busy = removeMutation.isPending || newCodeMutation.isPending;
  const back = () => {
    removeMutation.reset();
    newCodeMutation.reset();
    setStep('details');
  };
  const cancel = (
    <button
      type="button"
      onClick={back}
      disabled={busy}
      className="px-4 py-2 text-slate-400 hover:text-white transition-colors disabled:opacity-50"
    >
      Cancel
    </button>
  );
  const failed = (error: unknown, fallback: string) => (
    <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
      {reasonOf(error, fallback)}
    </p>
  );

  if (step === 'confirm-code') {
    return (
      <ModalShell
        open
        onClose={onClose}
        closeDisabled={busy}
        tone="dark"
        size="sm"
        title={`Get a new code for ${shownName}?`}
        footer={
          <div className="flex gap-3 justify-end">
            {cancel}
            <button
              type="button"
              onClick={() => newCodeMutation.mutate()}
              disabled={busy}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {newCodeMutation.isPending ? 'Getting code...' : 'Get new code'}
            </button>
          </div>
        }
      >
        <div className="space-y-5">
          <Lead icon={ArrowPathIcon} tone="caution">
            Use this if your {kind} can't connect, or you want to register it again.
          </Lead>
          <Facts
            items={[
              { icon: PauseCircleIcon, text: `${shownName} stops syncing straight away.` },
              { icon: KeyIcon, text: 'Enter the new code on it to reconnect. Your Polaris+ plan reconnects with it.' },
              { icon: ClockIcon, text: 'The code lasts 7 days, and you can find it here until it is used.' },
            ]}
          />
          {newCodeMutation.isError && failed(newCodeMutation.error, "We couldn't get a new code. Please try again.")}
        </div>
      </ModalShell>
    );
  }

  if (step === 'new-code' && newCodeMutation.data) {
    const fresh = newCodeMutation.data;
    return (
      <ModalShell
        open
        onClose={onClose}
        tone="dark"
        size="sm"
        title={`New code for ${shownName}`}
        footer={
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="flex items-start gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-slate-100">
            <PauseCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" aria-hidden="true" />
            <span>{shownName} has stopped syncing until you enter this code on it.</span>
          </p>
          {fresh.registration_code ? (
            <RegistrationCodePanel
              code={fresh.registration_code}
              expiresAt={fresh.registration_code_expires_at}
              steps="reconnect"
            />
          ) : (
            <p className="text-slate-300 text-sm">Open this device again to see its new code.</p>
          )}
        </div>
      </ModalShell>
    );
  }

  if (step === 'confirm-remove') {
    return (
      <ModalShell
        open
        onClose={onClose}
        closeDisabled={busy}
        tone="dark"
        size="sm"
        title={`Remove ${shownName}?`}
        footer={
          <div className="flex gap-3 justify-end">
            {cancel}
            <button
              type="button"
              onClick={() => removeMutation.mutate()}
              disabled={busy}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {removeMutation.isPending ? 'Removing...' : 'Remove'}
            </button>
          </div>
        }
      >
        <div className="space-y-5">
          <Lead icon={TrashIcon} tone="alarm">
            {shownName} stops syncing and leaves your account.
          </Lead>
          <Facts
            items={[
              { icon: MinusCircleIcon, text: "It no longer counts toward your plan's device limit." },
              { icon: PlusCircleIcon, text: 'To use it again, register it as a new device.' },
            ]}
          />
          {removeMutation.isError && failed(removeMutation.error, "We couldn't remove this device. Please try again.")}
        </div>
      </ModalShell>
    );
  }

  const waiting = device.status === 'pending' && !!device.registration_code;
  const line = statusLine(device, gateway);
  const details = (
    <div ref={bodyRef} tabIndex={-1} className="space-y-4 focus:outline-none">
      {/* The device, its status and when it was last heard from: one well. */}
      <div className="space-y-4 rounded-xl bg-slate-900/50 p-4">
        <div className="flex items-center gap-4">
          <DeviceGlyph device={device} size="lg" />
          <div className="min-w-0 space-y-1.5">
            <StatusChip device={device} />
            {line && <p className="text-sm text-slate-300">{line}</p>}
          </div>
        </div>
        {(device.last_seen_at || device.last_sync_at) && (
          <div className="grid grid-cols-2 gap-4">
            <Figure label="Last seen" value={device.last_seen_at} />
            <Figure label="Last sync" value={device.last_sync_at} />
          </div>
        )}
      </div>

      {waiting && device.registration_code && (
        // Only while it can still be used. Once the device registers, the
        // code is spent (issue #753). A device registered before needs
        // Re-register on the Scout.
        <RegistrationCodePanel
          code={device.registration_code}
          expiresAt={device.registration_code_expires_at}
          steps={gateway ? null : device.registered_at ? 'reconnect' : 'register'}
        />
      )}

      <dl className="space-y-3 rounded-xl bg-slate-900/50 px-4 py-3">
        <DetailRow label="Serial number"><CopyValue value={device.serial_number} label="Serial number" name="serial number" /></DetailRow>
        <DetailRow label="Device ID"><CopyValue value={device.id} label="Device ID" name="device ID" /></DetailRow>
        {device.registered_at && (
          <DetailRow label="Registered">
            <span className="text-sm text-white" title={fullTime(device.registered_at)}>
              {format(new Date(device.registered_at), 'd MMM yyyy')}
            </span>
          </DetailRow>
        )}
      </dl>
    </div>
  );

  return (
    <ModalShell
      open
      onClose={onClose}
      tone="dark"
      size={gateway && device.user_id ? 'lg' : 'md'}
      // The details list holds no input, so this cannot raise the keyboard. A
      // linked gateway's details sit behind a tab, so it keeps the default.
      initialFocusRef={gateway && device.user_id ? undefined : bodyRef}
      title={
        <InlineRename
          value={shownName}
          onSave={async (name) => { await updateMutation.mutateAsync(name); setShownName(name); }}
          label="Rename device"
          hint={device.device_type === 'scout' ? 'Also renames the boat on the Scout and in emails.' : undefined}
          display={(name) => <span>{name}</span>}
        />
      }
      description={kind}
      footer={
        <div className={`flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:gap-3 ${canGetNewCode ? 'sm:justify-between' : ''}`}>
          <button
            type="button"
            onClick={() => setStep('confirm-remove')}
            className="px-3 py-2 sm:-ml-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <TrashIcon className="w-4 h-4" />
            Remove from account
          </button>
          {canGetNewCode && (
            <button
              type="button"
              onClick={() => setStep('confirm-code')}
              className="px-4 py-2 border border-slate-600 text-slate-200 hover:bg-slate-700 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Get a new code
            </button>
          )}
        </div>
      }
    >
      {/* A linked Scout Gateway: Open SG, three tiles and tabs; these details under Details. */}
      {gateway && device.user_id
        ? <SgDevicePanel deviceId={device.id} boat={shownName} details={details} />
        : details}
    </ModalShell>
  );
}

export default DevicesPage;
